import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiUser, FiUsers, FiUserCheck, FiUserX, FiMessageSquare, FiPlus, FiTrash2, FiRefreshCw, FiGitBranch, FiPhone, FiSearch, FiMoon, FiSun, FiX } from 'react-icons/fi';
import { useApp } from '../../context/AppContext';
import { formatLastSeen } from '../../utils/timeUtils';
import { playDeleteSound } from '../../utils/notificationSound';
import { useNdTheme } from '../../utils/ndTheme';
import '../../styles/newDesign.css';
import CompanyStructureModal from './CompanyStructureModal';
import CompanyEmployeesListModal from './CompanyEmployeesListModal';
import OnboardingModal from './OnboardingModal';

const ND_CARD_COLORS = ['#2dd4bf', '#38bdf8', '#a78bfa', '#fbbf24', '#f0566a', '#34d399', '#f472b6', '#f97316'];

function ndColorOf(name) {
  const s = String(name || '?');
  let sum = 0;
  for (let i = 0; i < s.length; i += 1) sum += s.charCodeAt(i);
  return ND_CARD_COLORS[sum % ND_CARD_COLORS.length];
}

function ndInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0][0] || '';
  const second = parts[1] ? parts[1][0] : '';
  return (first + second).toUpperCase();
}

function capitalize(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

export default function EmployeesListModal({ open, onClose, isMobile = false, onOpenUserRights }) {
  const { state, dispatch } = useApp();
  const { isLight: ndIsLight, toggle: toggleNdTheme } = useNdTheme();
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState('all'); // 'all', 'online', 'offline'
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showCompanyStructure, setShowCompanyStructure] = useState(false);
  const [showCompanyEmployeesList, setShowCompanyEmployeesList] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [now, setNow] = useState(() => new Date());
  // Departments permissions (all mapped to role 'user')

  // pendingPrivateTargets — чтобы сразу показать иконку чата напротив пользователя,
  // пока сервер создаёт реальный приватный чат
  const [pendingPrivateTargets, setPendingPrivateTargets] = useState(new Set());

  // Закрытие модалки
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (!open) {
      setShowCompanyStructure(false);
      setShowCompanyEmployeesList(false);
      setShowOnboarding(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!window.socket) return;

    const refreshData = () => {
      window.socket.emit('get_all_users');
      window.socket.emit('get_online_users');
    };

    refreshData();

    const handleAllUsers = (users) => {
      setEmployees(users || []);
    };

    window.socket.on('all_users', handleAllUsers);

    return () => {
      window.socket.off('all_users', handleAllUsers);
    };
  }, [open]);


  // Когда список onlineUsers меняется — обновляем список пользователей
  useEffect(() => {
    if (!open) return;
    if (!window.socket || !window.socket.connected) return;
    window.socket.emit('get_all_users');
  }, [state.onlineUsers, open]);

  // Слушаем создание/приход чатов от сервера — чтобы убрать pending метку
  useEffect(() => {
    if (!window.socket) return;

    const onNewChat = (newChat) => {
      if (!newChat) return;
      if (newChat.isPrivate && Array.isArray(newChat.participants)) {
        const other = newChat.participants.find(p => p.id !== state.user?.id);
        if (other) {
          setPendingPrivateTargets(prev => {
            if (!prev.has(other.id)) return prev;
            const next = new Set(prev);
            next.delete(other.id);
            return next;
          });
        }
      }
    };

    window.socket.on('chat_created', onNewChat);
    window.socket.on('private_chat_created', onNewChat);

    return () => {
      window.socket.off('chat_created', onNewChat);
      window.socket.off('private_chat_created', onNewChat);
    };
  }, [state.user?.id]);

  // Функция для определения онлайн статуса пользователя
  const isUserOnline = (userId) => {
    return state.onlineUsers.some(user => user.id === userId);
  };

  // Функция обновления статусов пользователей
  const handleRefreshStatuses = async () => {
    if (!window.socket || isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      // Запрашиваем обновленные данные пользователей и онлайн статусы
      window.socket.emit('get_all_users');
      window.socket.emit('get_online_users');
      
      // Имитируем небольшую задержку для показа анимации
      setTimeout(() => {
        setIsRefreshing(false);
      }, 1000);
    } catch (error) {
      console.error('Ошибка при обновлении статусов:', error);
      setIsRefreshing(false);
    }
  };

  // Фильтрация сотрудников по имени, фамилии и статусу
  const filteredEmployees = employees.filter(emp => {
    const query = search.trim().toLowerCase();
    const isOnline = isUserOnline(emp.id);

    if (filter === 'online' && !isOnline) return false;
    if (filter === 'offline' && isOnline) return false;

    if (!query) return true;
    return (
      (emp.username && emp.username.toLowerCase().includes(query)) ||
      (emp.first_name && emp.first_name.toLowerCase().includes(query)) ||
      (emp.last_name && emp.last_name.toLowerCase().includes(query))
    );
  });

  const onlineCount = employees.filter(emp => isUserOnline(emp.id)).length;

  const chats = state.chats || [];
  const currentChatId = state.currentChat?.id;
  const [deletingChats, setDeletingChats] = useState(new Set());

  // Хелпер: определяем, что чат — приватный 1:1 (разные варианты с сервера)
  const isOneToOnePrivate = (chat) => {
    if (!chat) return false;
    if (chat.isPrivate) return true;
    if (chat.type === 'private') return true;
    const twoParticipants = Array.isArray(chat.participants) && chat.participants.length === 2;
    if (twoParticipants && !chat.isGroup) return true;
    if (typeof chat.name === 'string' && chat.name.trim().toLowerCase() === 'приватный чат') return true;
    return false;
  };

  // ВАЖНО: скрываем из секции "Ваши чаты" все приватные 1:1 чаты,
  // чтобы они не дублировались в списке чатов. (Иконка напротив сотрудника
  // показывает наличие переписки.)
  const visibleChats = (chats || []).filter(chat => !isOneToOnePrivate(chat));

  const handleSelectChat = (chat) => {
    handleClose();
    dispatch({ type: 'SET_CURRENT_CHAT', payload: chat });
    dispatch({ type: 'MARK_CHAT_AS_READ', payload: { chatId: chat.id, participants: chat.participants } });
    if (window.socket && window.socket.connected) {
      window.socket.emit('join_chat', chat.id);
      window.socket.emit('mark_chat_read', chat.id);
    }
    if (window.setMessengerView) window.setMessengerView('chat');
  };

  const handleCreateChat = () => {
    handleClose();
    dispatch({ type: 'TOGGLE_MODAL', payload: { modal: 'createChat', show: true } });
  };

  const handleDeleteChat = (chatId, chatName, event) => {
    event.stopPropagation();
    if (window.confirm(`Вы уверены, что хотите удалить чат "${chatName}"?`)) {
      playDeleteSound(0.3);
      setDeletingChats(prev => new Set([...prev, chatId]));
      dispatch({ type: 'DELETE_CHAT', payload: { chatId } });
      if (window.socket) window.socket.emit('delete_chat', { chatId });
      setTimeout(() => {
        setDeletingChats(prev => {
          const next = new Set(prev);
          next.delete(chatId);
          return next;
        });
      }, 2000);
    }
  };

  // Обработчик клика по сотруднику
  const handleSelect = (userId) => {
    handleClose();

    const otherUser = employees.find(e => e.id === userId);
    // Запомним последнюю цель приватного чата для заголовка
    dispatch({ type: 'SET_LAST_PRIVATE_TARGET', payload: otherUser });

    // Проверяем, есть ли приватный чат с этим пользователем
    const privateChat = (chats || []).find(
      chat => chat.isPrivate && chat.participants?.some(p => p.id === userId)
    );

    if (privateChat) {
      // Открываем существующий приватный чат
      dispatch({ type: 'SET_CURRENT_CHAT', payload: privateChat });
      dispatch({ type: 'MARK_CHAT_AS_READ', payload: { chatId: privateChat.id, participants: privateChat.participants } });
      if (window.socket && window.socket.connected) {
        window.socket.emit('join_chat', privateChat.id);
        window.socket.emit('mark_chat_read', privateChat.id);
      }
      if (window.setMessengerView) window.setMessengerView('chat');
    } else {
      // Если приватного чата ещё нет — помечаем пользователя как pending (покажем иконку рядом)
      setPendingPrivateTargets(prev => {
        const next = new Set(prev);
        next.add(userId);
        return next;
      });

      // Запрашиваем создание приватного чата. Не создаём временный чат и не переключаемся до подтверждения
      if (window.socket) {
        window.socket.emit('create_private_chat', { targetUserId: userId });
      }
    }
  };

  const handleCall = (emp, event) => {
    event.stopPropagation();
    if (!window.socket) return;
    dispatch({ type: 'SET_VOICE_CALL', payload: { status: 'outgoing', toUser: emp, isCaller: true } });
    window.socket.emit('voice_call:request', { toUserId: emp.id });
  };

  if (!open) return null;

  const clockTime = now.toLocaleTimeString('ru-RU');
  const clockWeekday = capitalize(now.toLocaleDateString('ru-RU', { weekday: 'long' }));
  const clockDate = now.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: isMobile ? 0 : 320,
        right: 0,
        bottom: 0,
        width: isMobile ? '100vw' : 'calc(100vw - 320px)',
        height: '100vh',
        background: 'rgba(4,10,13,0.72)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        zIndex: 100001
      }}
      onClick={handleClose}
    >
      <div
        className={`nd-empModal${isMobile ? ' nd-mobile' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="nd-empHead">
          <span className="nd-hIco"><FiUsers /></span>
          <h2>Сотрудники</h2>
          <div className="nd-empClock">
            <span className="nd-t nd-mono">{clockTime}</span>
            <i />
            <span className="nd-d">{clockWeekday}</span>
            <i />
            <span className="nd-d">{clockDate}</span>
          </div>
          <button
            type="button"
            className="nd-themeBtn"
            onClick={toggleNdTheme}
            title={ndIsLight ? 'Включить тёмную тему' : 'Включить светлую тему'}
          >
            {ndIsLight ? <FiMoon /> : <FiSun />}
          </button>
          <button
            type="button"
            className="nd-empClose"
            onClick={handleClose}
            aria-label="Закрыть"
            title="Закрыть"
          >
            <FiX />
          </button>
        </div>

        <div className="nd-empBody">
          {/* Мобильный полный набор; на десктопе — только структура */}
          {isMobile ? (
            <div className="nd-empMenu">
              <button type="button" className="nd-structBtn" onClick={() => setShowCompanyEmployeesList(true)}>
                <FiUsers size={18} />
                Список сотрудников
              </button>
              <button type="button" className="nd-structBtn" onClick={() => setShowCompanyStructure(true)}>
                <FiGitBranch size={18} />
                Структура компании
              </button>
              <button type="button" className="nd-structBtn" onClick={() => setShowOnboarding(true)}>
                <FiUserCheck size={18} />
                Онбординг
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="nd-structBtn"
              onClick={() => setShowCompanyStructure(true)}
              title="Структура компании"
            >
              <FiGitBranch size={18} />
              Структура компании
            </button>
          )}

          <div className="nd-chipRow">
            <button
              type="button"
              className={`nd-fChip${filter === 'all' ? ' on' : ''}`}
              onClick={() => setFilter('all')}
              title="Все сотрудники"
            >
              <FiUsers size={13} /> Все <span className="nd-cN">{employees.length}</span>
            </button>
            <button
              type="button"
              className={`nd-fChip${filter === 'online' ? ' on' : ''}`}
              onClick={() => setFilter('online')}
              title="Только онлайн"
            >
              <FiUserCheck size={13} /> Онлайн <span className="nd-cN">{onlineCount}</span>
            </button>
            <button
              type="button"
              className={`nd-fChip${filter === 'offline' ? ' on' : ''}`}
              onClick={() => setFilter('offline')}
              title="Только офлайн"
            >
              <FiUserX size={13} /> Офлайн <span className="nd-cN">{employees.length - onlineCount}</span>
            </button>
            <button
              type="button"
              className="nd-fChip create"
              onClick={handleCreateChat}
              title="Создать чат"
            >
              <FiPlus size={13} /> Создать чат
            </button>
          </div>

          {/* --- СЕКЦИЯ ЧАТОВ (скрываем 1:1 приватные чаты) --- */}
          <section>
            <div className="nd-secHead" style={{ marginBottom: 10 }}>
              <h3>Ваши чаты</h3>
            </div>
            <div className="nd-chatsPanel nd-glass">
              {visibleChats.length === 0 ? (
                <div className="nd-empEmpty">Нет чатов</div>
              ) : (
                visibleChats.map((chat, i) => (
                  <div
                    key={chat.id}
                    className={`nd-chatRow${chat.id === currentChatId ? ' nd-active' : ''}`}
                    style={{ animationDelay: `${Math.min(i * 60, 400)}ms`, opacity: deletingChats.has(chat.id) ? 0.5 : undefined }}
                    onClick={() => handleSelectChat(chat)}
                  >
                    <span className="nd-ci"><FiMessageSquare /></span>
                    <span className="nd-chName">{chat.name}</span>
                    {Number(chat.unread_count) > 0 && (
                      <span className="nd-chBadge">{chat.unread_count}</span>
                    )}
                    <button
                      type="button"
                      className="nd-del"
                      title="Удалить чат"
                      onClick={e => handleDeleteChat(chat.id, chat.name, e)}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))
              )}
            </div>
          </section>

          <section>
            <div className="nd-secHead" style={{ justifyContent: 'center', marginBottom: 12 }}>
              <h3>Все сотрудники</h3>
              <button
                type="button"
                className={`nd-refBtn${isRefreshing ? ' nd-spin' : ''}`}
                onClick={handleRefreshStatuses}
                disabled={isRefreshing}
                title="Обновить статусы пользователей"
              >
                <span className="nd-sp"><FiRefreshCw size={13} /></span>
                {isRefreshing ? 'Обновление…' : 'Обновить'}
              </button>
            </div>

            <div className="nd-empSearchW">
              <span className="nd-sIco"><FiSearch /></span>
              <input
                type="text"
                placeholder="Поиск по имени или фамилии…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
              />
            </div>

            <div style={{ height: 14 }} />

            <div className="nd-empGrid">
              {filteredEmployees.length === 0 ? (
                <div className="nd-empEmpty">Ничего не найдено</div>
              ) : (
                filteredEmployees.map((emp, index) => {
                  const isOnline = isUserOnline(emp.id);
                  const displayName = emp.username || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Без имени';
                  const cardColor = ndColorOf(displayName);
                  const unread = state.unreadByUser?.[emp.id] || 0;
                  const missed = (state.missedCallsByUser || {})[emp.id] || 0;
                  const privateChat = (chats || []).find(chat =>
                    isOneToOnePrivate(chat) && chat.participants?.some(p => p.id === emp.id)
                  );
                  // Показываем метку, если переписка уже есть либо только что инициирована
                  const hasChat = Boolean(privateChat) || pendingPrivateTargets.has(emp.id) || unread > 0;

                  return (
                    <div
                      key={emp.id}
                      className="nd-empCard nd-glass"
                      style={{ '--cc': cardColor, animationDelay: `${Math.min(index * 45, 500)}ms` }}
                      onClick={() => handleSelect(emp.id)}
                    >
                      <div className="nd-avWrap">
                        <div className="nd-av">
                          {emp.avatar ? <img src={emp.avatar} alt="" /> : (ndInitials(displayName) || <FiUser />)}
                        </div>
                        <span className={`nd-avSt ${isOnline ? 'nd-on' : 'nd-off'}`} />
                      </div>

                      <b>{displayName}</b>

                      <span className={`nd-stPill ${isOnline ? 'nd-on' : 'nd-off'}`}>
                        <i />
                        {formatLastSeen(emp.last_seen, isOnline)}
                      </span>

                      {(unread > 0 || missed > 0 || hasChat) && (
                        <div className="nd-empBadges">
                          {hasChat && unread === 0 && (
                            <span className="nd-empBadge nd-chatMark" title="Есть переписка"><FiMessageSquare size={11} /> переписка</span>
                          )}
                          {unread > 0 && (
                            <span className="nd-empBadge" title="Непрочитанные сообщения">{unread} сообщ.</span>
                          )}
                          {missed > 0 && (
                            <span className="nd-empBadge nd-miss" title="Пропущенные звонки">{missed} пропущ. звонков</span>
                          )}
                        </div>
                      )}

                      {/* Кнопки «Написать» и «Позвонить» — не показываем себе */}
                      {state.user?.id !== emp.id && (
                        <div className="nd-cardBtns">
                          <button
                            type="button"
                            className="nd-wBtn"
                            title="Написать сообщение"
                            onClick={(e) => { e.stopPropagation(); handleSelect(emp.id); }}
                          >
                            <FiMessageSquare size={14} /> Написать
                          </button>
                          <button
                            type="button"
                            className="nd-cBtn"
                            title="Позвонить"
                            onClick={(e) => handleCall(emp, e)}
                          >
                            <FiPhone size={14} /> Позвонить
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </div>
      </div>
      {showCompanyStructure &&
        (isMobile
          ? ReactDOM.createPortal(
              <CompanyStructureModal
                open={showCompanyStructure}
                onClose={() => setShowCompanyStructure(false)}
                isMobile
              />,
              document.body
            )
          : (
              <CompanyStructureModal
                open={showCompanyStructure}
                onClose={() => setShowCompanyStructure(false)}
                isMobile={false}
              />
            ))}
      {showCompanyEmployeesList &&
        (isMobile
          ? ReactDOM.createPortal(
              <CompanyEmployeesListModal
                open={showCompanyEmployeesList}
                onClose={() => setShowCompanyEmployeesList(false)}
                isMobile
                onOpenUserRights={onOpenUserRights}
              />,
              document.body
            )
          : (
              <CompanyEmployeesListModal
                open={showCompanyEmployeesList}
                onClose={() => setShowCompanyEmployeesList(false)}
                isMobile={false}
                onOpenUserRights={onOpenUserRights}
              />
            ))}
      {showOnboarding &&
        (isMobile
          ? ReactDOM.createPortal(
              <OnboardingModal
                open={showOnboarding}
                onClose={() => setShowOnboarding(false)}
                isMobile
              />,
              document.body
            )
          : (
              <OnboardingModal
                open={showOnboarding}
                onClose={() => setShowOnboarding(false)}
                isMobile={false}
              />
            ))}
    </div>
  );
}
